import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import AppShell from "../components/AppShell";
import TopBar from "../components/TopBar";
import LoadingScreen from "../components/LoadingScreen";
import ErrorState from "../components/ErrorState";

function Podium({ players }) {
  const order = [players[1], players[0], players[2]]; // 2nd, 1st, 3rd
  const heights = [88, 120, 72];
  const colors = ["#C0C0C0", "#FFD700", "#CD7F32"];
  const medals = ["🥈", "🥇", "🥉"];
  return (
    <div className="flex items-end justify-center gap-2 mb-6">
      {order.map((p, i) =>
        p ? (
          <div key={p.user_id} className="flex flex-col items-center flex-1">
            <span className="text-3xl">{p.avatar}</span>
            <span className="font-black text-[#4A3728] text-sm truncate max-w-[90px]">{p.name}</span>
            <span className="text-xs font-bold text-[#8B7355]">{p.total_xp} XP</span>
            <div
              className="w-full rounded-t-[16px] mt-1 flex items-start justify-center pt-2"
              style={{ height: heights[i], background: colors[i] }}
            >
              <span className="text-2xl">{medals[i]}</span>
            </div>
          </div>
        ) : (
          <div key={i} className="flex-1" />
        )
      )}
    </div>
  );
}

function Row({ p, weekly }) {
  return (
    <div
      data-testid={`leaderboard-row-${p.user_id}`}
      className={`flex items-center gap-3 rounded-[18px] px-4 py-3 border-2 ${
        p.is_current_user ? "bg-[#FFE0EC] border-[#FF69B4]" : "bg-white border-[#FFEAD6]"
      }`}
    >
      <span className="font-black text-[#8B7355] w-6 text-center">{p.rank}</span>
      <span className="text-2xl">{p.avatar}</span>
      <div className="flex-1 min-w-0">
        <p className="font-black text-[#4A3728] truncate">
          {p.name} {p.is_current_user && "(você)"}
        </p>
        {!weekly && (
          <p className="text-xs font-bold text-[#8B7355]">
            Nível {p.level} {p.streak_days ? `· 🔥 ${p.streak_days}` : ""}
          </p>
        )}
      </div>
      <span className="font-black text-[#FF69B4]">{weekly ? p.weekly_xp : p.total_xp} XP</span>
    </div>
  );
}

export default function Leaderboard() {
  const { userId } = useAuth();
  const [tab, setTab] = useState("geral");

  const generalQ = useQuery({
    queryKey: ["leaderboard", userId],
    queryFn: () => joyApi.leaderboard(userId),
    enabled: !!userId,
  });
  const weeklyQ = useQuery({
    queryKey: ["leaderboard-weekly", userId],
    queryFn: () => joyApi.weeklyLeaderboard(userId),
    enabled: !!userId && tab === "semanal",
  });

  const weekly = tab === "semanal";
  const q = weekly ? weeklyQ : generalQ;

  if (generalQ.isLoading) return <LoadingScreen />;

  const list = q.data?.leaderboard || [];
  const rank = q.data?.current_user_rank;

  return (
    <AppShell>
      <TopBar title="🏆 Ranking" to="/" testId="leaderboard-topbar" />

      <div className="flex gap-2 mb-5 bg-white rounded-[18px] p-1.5 border-2 border-[#FFEAD6]">
        <button
          data-testid="tab-geral"
          onClick={() => setTab("geral")}
          className={`flex-1 rounded-[14px] py-2.5 font-black joy-press ${tab === "geral" ? "bg-[#FF69B4] text-white" : "text-[#8B7355]"}`}
        >
          🌟 Geral
        </button>
        <button
          data-testid="tab-semanal"
          onClick={() => setTab("semanal")}
          className={`flex-1 rounded-[14px] py-2.5 font-black joy-press ${tab === "semanal" ? "bg-[#FF69B4] text-white" : "text-[#8B7355]"}`}
        >
          📅 Semanal
        </button>
      </div>

      {q.isError ? (
        <ErrorState onRetry={q.refetch} />
      ) : q.isLoading ? (
        <p className="text-center font-bold text-[#8B7355] py-10">Carregando... 🦊</p>
      ) : (
        <>
          {rank && (
            <div data-testid="current-user-rank" className="text-center bg-[#FFF3B0] rounded-[18px] py-3 mb-5 font-black text-[#8B6B00]">
              Você está em #{rank} 🎉
            </div>
          )}
          {!weekly && list.length >= 3 && <Podium players={list.slice(0, 3)} />}
          <div className="flex flex-col gap-2" data-testid="leaderboard-list">
            {list.map((p) => (
              <Row key={p.user_id} p={p} weekly={weekly} />
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
